import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { apiUrl } from '../../../core/config/api-endpoints';
import {
  SupportAssigneeListResponse,
  SupportCategoryOption,
  SupportRaisedVia,
  SupportRequesterListResponse,
  SupportServiceRequestListResponse,
  SupportTicketChangeAssigneePayload,
  SupportTicketChangeCategoryPayload,
  SupportTicketChangePriorityPayload,
  SupportTicketClosePayload,
  SupportTicketCreatePayload,
  SupportTicketDetail,
  SupportTicketExportResponse,
  SupportTicketListQuery,
  SupportTicketListResponse,
  SupportTicketMessage,
  SupportTicketMessagePayload,
  SupportTicketInternalNote,
  SupportTicketStats,
} from '../models/help-support-tickets';

@Injectable({ providedIn: 'root' })
export class HelpSupportTicketsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = apiUrl('/portal/support-tickets');

  listTickets(query: SupportTicketListQuery): Observable<SupportTicketListResponse> {
    return this.http.post<SupportTicketListResponse>(`${this.baseUrl}/search`, {
      page: query.page,
      pageSize: query.pageSize,
      search: query.search?.trim() || null,
      statuses: query.statuses ?? [],
      priorities: query.priorities ?? [],
      categoryIds: query.categoryIds ?? [],
      raisedVia: query.raisedVia ?? [],
      assigneeUserIds: query.assigneeUserIds ?? [],
      clientIds: query.clientIds ?? [],
      cities: query.cities ?? [],
      branchIds: query.branchIds ?? [],
      createdFrom: query.createdFrom ?? null,
      createdTo: query.createdTo ?? null,
      slaCrossed: query.slaCrossed === true,
      slaWithin: query.slaWithin === true,
      slaDueWithinHour: query.slaDueWithinHour === true,
      unassigned: query.unassigned === true,
    });
  }

  downloadTickets(query: SupportTicketListQuery): Observable<SupportTicketExportResponse> {
    return this.http.post<SupportTicketExportResponse>(`${this.baseUrl}/download`, {
      page: query.page,
      pageSize: query.pageSize,
      search: query.search?.trim() || null,
      statuses: query.statuses ?? [],
      priorities: query.priorities ?? [],
      categoryIds: query.categoryIds ?? [],
      raisedVia: query.raisedVia ?? [],
      assigneeUserIds: query.assigneeUserIds ?? [],
      clientIds: query.clientIds ?? [],
      cities: query.cities ?? [],
      branchIds: query.branchIds ?? [],
      createdFrom: query.createdFrom ?? null,
      createdTo: query.createdTo ?? null,
      slaCrossed: query.slaCrossed === true,
      slaWithin: query.slaWithin === true,
      slaDueWithinHour: query.slaDueWithinHour === true,
      unassigned: query.unassigned === true,
    });
  }

  getStats(): Observable<SupportTicketStats> {
    return this.http.get<SupportTicketStats>(`${this.baseUrl}/stats`);
  }

  listCategories(): Observable<SupportCategoryOption[]> {
    return this.http.get<SupportCategoryOption[]>(`${this.baseUrl}/categories`);
  }

  listAssignees(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Observable<SupportAssigneeListResponse> {
    return this.http.get<SupportAssigneeListResponse>(`${this.baseUrl}/assignees`, {
      params: {
        page: String(params?.page ?? 1),
        pageSize: String(params?.pageSize ?? 100),
        ...(params?.search?.trim() ? { search: params.search.trim() } : {}),
      },
    });
  }

  listRequestors(params: {
    raisedVia: SupportRaisedVia;
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: number;
  }): Observable<SupportRequesterListResponse> {
    return this.http.get<SupportRequesterListResponse>(`${this.baseUrl}/requestors`, {
      params: {
        raisedVia: params.raisedVia,
        page: String(params.page ?? 1),
        pageSize: String(params.pageSize ?? 30),
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        ...(params.clientId != null ? { clientId: String(params.clientId) } : {}),
      },
    });
  }

  listServiceRequests(params: {
    raisedVia: SupportRaisedVia;
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: number;
    partnerUserId?: number;
  }): Observable<SupportServiceRequestListResponse> {
    return this.http.get<SupportServiceRequestListResponse>(`${this.baseUrl}/service-requests`, {
      params: {
        raisedVia: params.raisedVia,
        page: String(params.page ?? 1),
        pageSize: String(params.pageSize ?? 25),
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        ...(params.clientId != null ? { clientId: String(params.clientId) } : {}),
        ...(params.partnerUserId != null ? { partnerUserId: String(params.partnerUserId) } : {}),
      },
    });
  }

  getTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.http.get<SupportTicketDetail>(`${this.baseUrl}/${encodeURIComponent(ticketId)}`);
  }

  createTicket(payload: SupportTicketCreatePayload): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(this.baseUrl, payload);
  }

  createMessage(
    ticketId: string,
    payload: SupportTicketMessagePayload,
  ): Observable<SupportTicketMessage> {
    return this.http.post<SupportTicketMessage>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/messages`,
      payload,
    );
  }

  createInternalNote(
    ticketId: string,
    payload: SupportTicketMessagePayload,
  ): Observable<SupportTicketInternalNote> {
    return this.http.post<SupportTicketInternalNote>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/internal-notes`,
      payload,
    );
  }

  changeAssignee(
    ticketId: string,
    payload: SupportTicketChangeAssigneePayload,
  ): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/change-assignee`,
      payload,
    );
  }

  changeCategory(
    ticketId: string,
    payload: SupportTicketChangeCategoryPayload,
  ): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/change-category`,
      payload,
    );
  }

  changePriority(
    ticketId: string,
    payload: SupportTicketChangePriorityPayload,
  ): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/change-priority`,
      payload,
    );
  }

  assignToMe(ticketId: string): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/assign-to-me`,
      {},
    );
  }

  closeTicket(
    ticketId: string,
    payload: SupportTicketClosePayload,
  ): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/close`,
      payload,
    );
  }

  reopenTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.http.post<SupportTicketDetail>(
      `${this.baseUrl}/${encodeURIComponent(ticketId)}/reopen`,
      {},
    );
  }
}
